import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import Navbar      from '../components/Navbar';
import StatsCard   from '../components/StatsCard';
import BookingCard from '../components/BookingCard';
import { bookingsApi, adminApi, schoolsApi, eventsApi } from '../services/api';

const NAV = [
  { id: 'overview',  icon: '📊', label: 'Overview' },
  { id: 'bookings',  icon: '📅', label: 'Bookings' },
  { id: 'events',    icon: '🎉', label: 'Events' },
  { id: 'settings', icon: '⚙️', label: 'Settings' },
];
const PAGE_TITLE = {
  overview: 'Overview', bookings: 'Bookings', settings: 'Settings',
  'cancel-booking': 'Cancel Booking',
  events: 'Events', 'create-event': 'Create Event', 'edit-event': 'Edit Event',
};

const FACILITIES = ['Playground','Library','Cafeteria','Nap Room','Art Studio','Music Room','Swimming Pool','Sports Field'];

export default function SchoolDashboard() {
  const { user, logout } = useAuth();
  const [tab,      setTab]      = useState('overview');
  const [stats,    setStats]    = useState({});
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(o => !o);

  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason,  setCancelReason]  = useState('');
  const [prevTab,       setPrevTab]       = useState('bookings');
  const [bookingFilter, setBookingFilter] = useState('all'); // all | pending | accepted | cancelled

  // Events state
  const STORAGE_TOTAL = 100 * 1024 * 1024;
  const [events,        setEvents]        = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [storageUsed,   setStorageUsed]   = useState(0);
  const [evtForm,       setEvtForm]       = useState({ name: '', date: '', isPublic: true });
  const [evtImages,     setEvtImages]     = useState([]);
  const [evtMsg,        setEvtMsg]        = useState('');
  const [evtCreating,   setEvtCreating]   = useState(false);
  const evtFileRef = useRef(null);

  // Edit event state
  const [editEvt,       setEditEvt]       = useState(null);  // full event object being edited
  const [editForm,      setEditForm]      = useState({ name: '', date: '', isPublic: true });
  const [editNewImages, setEditNewImages] = useState([]);    // new images to upload
  const [editDelPaths,  setEditDelPaths]  = useState([]);    // existing image paths to delete
  const [editMsg,       setEditMsg]       = useState('');
  const [editSaving,    setEditSaving]    = useState(false);
  const editFileRef = useRef(null);
  const [copiedId,      setCopiedId]      = useState(null);  // for copy-link feedback

  const EMPTY_SETTINGS = { pricePerHour: '', capacity: '', ageRange: '', operatingHours: '', description: '', facilities: [] };
  const [settings,        setSettings]        = useState(EMPTY_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving,  setSettingsSaving]  = useState(false);
  const [settingsMsg,     setSettingsMsg]     = useState('');

  const schoolId = user?.schoolId;

  const loadEvents = useCallback(async () => {
    if (!schoolId) return;
    setEventsLoading(true);
    try {
      const d = await eventsApi.getAll({ schoolId, userId: user.userId });
      setEvents(d.events || []);
      setStorageUsed(d.usedBytes || 0);
    } catch (_) {}
    setEventsLoading(false);
  }, [schoolId, user]);

  const handleDeleteEvent = async (evId) => {
    if (!window.confirm('Delete this event and all its photos?')) return;
    try {
      await eventsApi.remove({ userId: user.userId, schoolId, eventId: evId });
      loadEvents();
    } catch (err) { alert(err.message); }
  };

  const shareEvent = (evId, evName) => {
    const link = `${window.location.origin}?event=${evId}`;
    const msg  = `Check out *${evName}* — photos from our school! 🎉\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };
  const copyEventLink = (evId) => {
    const link = `${window.location.origin}?event=${evId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(evId);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      // fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = link; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedId(evId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const openEditEvent = (ev) => {
    setEditEvt(ev);
    setEditForm({ name: ev.name, date: ev.date, isPublic: ev.isPublic });
    setEditNewImages([]);
    setEditDelPaths([]);
    setEditMsg('');
    setTab('edit-event');
  };

  const handleEditImagePick = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const promises = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = ev => resolve({ preview: ev.target.result, data: ev.target.result, name: file.name, size: file.size });
      reader.readAsDataURL(file);
    }));
    Promise.all(promises).then(imgs => setEditNewImages(prev => [...prev, ...imgs]));
    e.target.value = '';
  };

  const handleSaveEdit = async () => {
    if (!editEvt) return;
    if (!editForm.name.trim()) { setEditMsg('Event name is required'); return; }
    if (!editForm.date)        { setEditMsg('Event date is required');  return; }
    setEditSaving(true); setEditMsg('');
    try {
      // Update metadata and delete marked images
      await eventsApi.update({
        userId: user.userId, schoolId, eventId: editEvt.id,
        name: editForm.name.trim(), date: editForm.date, isPublic: editForm.isPublic,
        deletePaths: editDelPaths,
      });
      // Upload new images
      for (let i = 0; i < editNewImages.length; i++) {
        setEditMsg(`Uploading photo ${i + 1} of ${editNewImages.length}\u2026`);
        await eventsApi.uploadImage({
          userId: user.userId, schoolId, eventId: editEvt.id,
          imageData: editNewImages[i].data, filename: editNewImages[i].name,
        });
      }
      setEditMsg('');
      setTab('events');
      loadEvents();
    } catch (err) { setEditMsg('\u274c ' + err.message); }
    setEditSaving(false);
  };
  const handleEvtImagePick = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const promises = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = ev => resolve({ preview: ev.target.result, data: ev.target.result, name: file.name, size: file.size });
      reader.readAsDataURL(file);
    }));
    Promise.all(promises).then(imgs => setEvtImages(prev => [...prev, ...imgs]));
    e.target.value = '';
  };

  const handleCreateEvent = async () => {
    if (!evtForm.name.trim()) { setEvtMsg('Event name is required'); return; }
    if (!evtForm.date)        { setEvtMsg('Event date is required');  return; }
    setEvtCreating(true); setEvtMsg('');
    try {
      const { eventId } = await eventsApi.create({
        userId: user.userId, schoolId,
        name: evtForm.name.trim(), date: evtForm.date, isPublic: evtForm.isPublic,
      });
      for (let i = 0; i < evtImages.length; i++) {
        setEvtMsg(`Uploading photo ${i + 1} of ${evtImages.length}…`);
        await eventsApi.uploadImage({
          userId: user.userId, schoolId, eventId,
          imageData: evtImages[i].data, filename: evtImages[i].name,
        });
      }
      setEvtForm({ name: '', date: '', isPublic: true });
      setEvtImages([]);
      setEvtMsg('');
      setTab('events');
      loadEvents();
    } catch (err) { setEvtMsg('❌ ' + err.message); }
    setEvtCreating(false);
  };

  const loadSchoolSettings = useCallback(async () => {
    if (!schoolId) return;
    setSettingsLoading(true);
    setSettingsMsg('');
    try {
      const d = await schoolsApi.getOne(schoolId);
      const s = d.school || d;
      setSettings({
        pricePerHour:   s.pricePerHour  ?? s.pricePerDay ?? '',
        capacity:       s.capacity      ?? '',
        ageRange:       s.ageRange      ?? '',
        operatingHours: s.operatingHours ?? '',
        description:    s.description   ?? '',
        facilities:     s.facilities    ?? [],
      });
    } catch (_) {}
    setSettingsLoading(false);
  }, [schoolId]);

  const loadStats = useCallback(async () => {
    if (!schoolId) return;
    try {
      const d = await adminApi.getStats({ userId: user.userId, userType: 'school', schoolId });
      setStats(d);
    } catch (_) {}
  }, [user, schoolId]);

  const loadBookings = useCallback(async (statusFilter) => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = { userId: user.userId, userType: 'school', schoolId };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const d = await bookingsApi.getAll(params);
      setBookings(d.bookings || []);
    } catch (_) {}
    setLoading(false);
  }, [user, schoolId]);

  useEffect(() => {
    if (tab === 'settings') { loadSchoolSettings(); return; }
    if (tab === 'events' || tab === 'create-event' || tab === 'edit-event') { loadEvents(); return; }
    loadStats();
    loadBookings();
  }, [tab]); // eslint-disable-line

  const handleAccept = async (booking) => {
    setSaving(true);
    try {
      await bookingsApi.updateStatus({ bookingId: booking.id, status: 'accepted', schoolId });
      loadBookings();
      loadStats();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const openCancelPage = (booking) => {
    setCancelBooking(booking);
    setCancelReason('');
    setPrevTab(tab);
    setTab('cancel-booking');
  };

  const handleCancel = async () => {
    if (!cancelBooking) return;
    setSaving(true);
    try {
      await bookingsApi.updateStatus({
        bookingId:    cancelBooking.id,
        status:       'cancelled',
        schoolId,
        cancelReason: cancelReason,
      });
      setCancelBooking(null);
      setCancelReason('');
      setTab(prevTab);
      loadBookings();
      loadStats();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const counts = {
    pending:   bookings.filter(b => b.status === 'pending').length,
    accepted:  bookings.filter(b => b.status === 'accepted').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };

  return (
    <div className="dash-shell">
      {/* Mobile backdrop */}
      <div className={`sidebar-backdrop${sidebarOpen ? ' visible' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Flogo_la.png?alt=media"
            alt="LittleAura"
            className="sidebar-logo-img"
          />
          <div className="logo-sub">School Dashboard</div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section-label">Bookings</div>
          {NAV.map(n => (
            <div key={n.id} className={`nav-item${tab === n.id ? ' active' : ''}`} onClick={() => { setTab(n.id); setSidebarOpen(false); }}>
              <span className="nav-icon">{n.icon}</span>{n.label}
              {n.id === 'bookings' && stats.pendingBookings > 0 && (
                <span style={{ marginLeft: 'auto', background: 'var(--warning)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                  {stats.pendingBookings}
                </span>
              )}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar" style={{ background: '#0369a1' }}>{initials}</div>
            <div className="sidebar-user-info">
              <div className="name">{user?.name}</div>
              <div className="role">School User</div>
            </div>
          </div>
          <button className="btn btn-outline btn-sm btn-full" style={{ color: 'rgba(255,255,255,.6)', borderColor: 'rgba(255,255,255,.15)' }} onClick={logout}>Sign Out</button>
        </div>
      </aside>

      {/* Main */}
      <div className="dash-main">
        <Navbar title={PAGE_TITLE[tab] || 'Dashboard'} onMenuToggle={toggleSidebar} />

        <div className="dash-content">
          {!schoolId ? (
            <div className="empty-state">
              <span className="empty-icon">⚠️</span>
              <h3>No school assigned</h3>
              <p>Your account is not linked to any school yet. Please contact the administrator.</p>
            </div>
          ) : (
            <>
              {/* ── OVERVIEW ────────────────────────────────────────── */}
              {tab === 'overview' && (
                <>
                  <p className="page-sub">Manage incoming booking requests for your school.</p>
                  <div className="stats-grid">
                    <StatsCard icon="📅" label="Total Bookings"    value={stats.totalBookings}     color="blue"   />
                    <StatsCard icon="⏳" label="Pending Requests"  value={stats.pendingBookings}   color="yellow" />
                    <StatsCard icon="✅" label="Accepted"          value={stats.acceptedBookings}  color="green"  />
                    <StatsCard icon="❌" label="Cancelled"         value={stats.cancelledBookings} color="red"    />
                  </div>

                  {/* Recent pending bookings */}
                  {stats.pendingBookings > 0 && (
                    <div className="section mt-16">
                      <div className="section-header">
                        <h3>⏳ Requires Attention</h3>
                        <button className="btn btn-outline btn-sm" onClick={() => { setTab('bookings'); setBookingFilter('pending'); }}>View All Pending</button>
                      </div>
                      <div className="section-body">
                        {bookings.filter(b => b.status === 'pending').slice(0, 3).map(b => (
                          <BookingCard key={b.id} booking={b} variant="school" onAccept={handleAccept} onCancel={openCancelPage} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── BOOKINGS (All / Pending / Accepted / Cancelled) ── */}
              {tab === 'bookings' && (
                <>
                  {/* Filter tabs */}
                  <div className="bk-filter-row">
                    {[
                      { id: 'all',       label: 'All' },
                      { id: 'pending',   label: `⏳ Pending${counts.pending   > 0 ? ` (${counts.pending})`   : ''}` },
                      { id: 'accepted',  label: `✅ Accepted${counts.accepted  > 0 ? ` (${counts.accepted})`  : ''}` },
                      { id: 'cancelled', label: `❌ Cancelled${counts.cancelled > 0 ? ` (${counts.cancelled})` : ''}` },
                    ].map(f => (
                      <button
                        key={f.id}
                        className={`bk-filter-btn${bookingFilter === f.id ? ' active' : ''}`}
                        onClick={() => setBookingFilter(f.id)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {loading ? <div className="spinner" /> : (() => {
                    const filtered = bookingFilter === 'all'
                      ? bookings
                      : bookings.filter(b => b.status === bookingFilter);
                    if (filtered.length === 0) {
                      const msgs = {
                        all:       { icon: '📅', title: 'No bookings yet',      sub: 'Bookings will appear here once parents start booking.' },
                        pending:   { icon: '🎉', title: 'All clear!',            sub: 'No pending bookings right now.' },
                        accepted:  { icon: '📅', title: 'No accepted bookings',  sub: 'Accepted bookings will appear here.' },
                        cancelled: { icon: '✅', title: 'No cancellations',      sub: 'Cancelled bookings will appear here.' },
                      };
                      const m = msgs[bookingFilter];
                      return (
                        <div className="empty-state">
                          <span className="empty-icon">{m.icon}</span>
                          <h3>{m.title}</h3>
                          <p>{m.sub}</p>
                        </div>
                      );
                    }
                    return (
                      <div className="booking-grid">
                        {filtered.map(b => (
                          <BookingCard key={b.id} booking={b} variant="school"
                            onAccept={b.status === 'pending' ? handleAccept : undefined}
                            onCancel={b.status !== 'cancelled' ? openCancelPage : undefined} />
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}


              {/* ── SETTINGS ───────────────────────────────────── */}
              {tab === 'settings' && (
                <div className="form-page">
                  <h2 className="form-page-title">⚙️ School Settings</h2>
                  <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 20 }}>Update your school's operational details.</p>
                  {settingsLoading ? <div className="spinner" /> : (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Charge per Hour (₹)</label>
                          <input className="form-input" type="number" min="0"
                            value={settings.pricePerHour}
                            onChange={e => setSettings(s => ({ ...s, pricePerHour: e.target.value }))}
                            placeholder="0" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Capacity</label>
                          <input className="form-input" type="number" min="1"
                            value={settings.capacity}
                            onChange={e => setSettings(s => ({ ...s, capacity: e.target.value }))}
                            placeholder="30" />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Age Range</label>
                          <input className="form-input"
                            value={settings.ageRange}
                            onChange={e => setSettings(s => ({ ...s, ageRange: e.target.value }))}
                            placeholder="0–5 years" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Operating Hours</label>
                          <input className="form-input"
                            value={settings.operatingHours}
                            onChange={e => setSettings(s => ({ ...s, operatingHours: e.target.value }))}
                            placeholder="8:00 AM – 5:00 PM" />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea className="form-textarea"
                          value={settings.description}
                          onChange={e => setSettings(s => ({ ...s, description: e.target.value }))}
                          placeholder="Brief description about the school…" />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Facilities</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                          {FACILITIES.map(f => (
                            <label key={f} style={{
                              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                              padding: '5px 12px', borderRadius: 20,
                              border: `1.5px solid ${settings.facilities.includes(f) ? 'var(--primary)' : 'var(--gray-200)'}`,
                              background: settings.facilities.includes(f) ? 'var(--primary-light)' : 'var(--white)',
                              fontSize: 13, transition: 'all .15s',
                            }}>
                              <input type="checkbox" style={{ display: 'none' }}
                                checked={settings.facilities.includes(f)}
                                onChange={() => setSettings(s => ({
                                  ...s,
                                  facilities: s.facilities.includes(f)
                                    ? s.facilities.filter(x => x !== f)
                                    : [...s.facilities, f],
                                }))}
                              />
                              {f}
                            </label>
                          ))}
                        </div>
                      </div>

                      {settingsMsg && (
                        <div style={{ padding: '10px 14px', borderRadius: 8, marginTop: 8,
                          background: settingsMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2',
                          color: settingsMsg.startsWith('✅') ? '#166534' : '#991b1b',
                          fontSize: 14 }}>
                          {settingsMsg}
                        </div>
                      )}

                      <div className="form-actions" style={{ marginTop: 20 }}>
                        <button className="btn btn-primary" disabled={settingsSaving}
                          onClick={async () => {
                            setSettingsSaving(true);
                            setSettingsMsg('');
                            try {
                              await schoolsApi.update({
                                schoolId,
                                pricePerHour:   Number(settings.pricePerHour || 0),
                                capacity:       Number(settings.capacity || 0),
                                ageRange:       settings.ageRange,
                                operatingHours: settings.operatingHours,
                                description:    settings.description,
                                facilities:     settings.facilities,
                              });
                              setSettingsMsg('✅ Settings saved successfully!');
                            } catch (err) {
                              setSettingsMsg('❌ ' + err.message);
                            }
                            setSettingsSaving(false);
                          }}>
                          {settingsSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── EVENTS ─────────────────────────────────────── */}
              {tab === 'events' && (
                <>
                  {/* Storage bar */}
                  <div className="evt-storage-bar">
                    <div className="evt-storage-label">
                      <span>Storage used</span>
                      <span>{(storageUsed / (1024 * 1024)).toFixed(1)} MB / 100 MB</span>
                    </div>
                    <div className="evt-storage-track">
                      <div className="evt-storage-fill" style={{ width: `${Math.min(100, storageUsed / STORAGE_TOTAL * 100)}%` }} />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <button className="btn btn-primary" onClick={() => { setEvtMsg(''); setTab('create-event'); }}>
                      + Create Event
                    </button>
                  </div>

                  {eventsLoading ? <div className="spinner" /> : events.length === 0 ? (
                    <div className="empty-state">
                      <span className="empty-icon">🎉</span>
                      <h3>No events yet</h3>
                      <p>Create your first event to share school memories with parents.</p>
                    </div>
                  ) : (
                    <div className="evt-list">
                      {events.map(ev => (
                        <div key={ev.id} className="evt-card">
                          <div className="evt-card-thumb">
                            {ev.images && ev.images[0]
                              ? <img src={ev.images[0].url} alt={ev.name} />
                              : <span style={{ fontSize: 36 }}>📷</span>}
                          </div>
                          <div className="evt-card-body">
                            <div className="evt-card-top">
                              <span className="evt-card-name">{ev.name}</span>
                              <span className={`badge ${ev.isPublic ? 'badge-accepted' : 'badge-cancelled'}`} style={{ fontSize: 11 }}>
                                {ev.isPublic ? '🌐 Public' : '🔒 Private'}
                              </span>
                            </div>
                            <div className="evt-card-date">📅 {ev.date}</div>
                            <div className="evt-card-count">📷 {(ev.images || []).length} photo{(ev.images || []).length !== 1 ? 's' : ''}</div>
                            <div className="evt-card-actions">
                              <button className="btn btn-outline btn-sm" onClick={() => openEditEvent(ev)}>✏️ Edit</button>
                              {ev.isPublic && (
                                <button className="btn btn-outline btn-sm" onClick={() => shareEvent(ev.id, ev.name)}>📲 Share</button>
                              )}
                              <button
                                className={`btn btn-outline btn-sm${copiedId === ev.id ? ' btn-copied' : ''}`}
                                onClick={() => copyEventLink(ev.id)}
                              >
                                {copiedId === ev.id ? '\u2713 Copied!' : '\ud83d� Copy Link'}
                              </button>
                              <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => handleDeleteEvent(ev.id)}>🗑️ Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── CREATE EVENT ──────────────────────────────────── */}
              {tab === 'create-event' && (
                <div className="form-page">
                  <button className="btn btn-ghost back-btn" onClick={() => setTab('events')}>← Back to Events</button>
                  <h2 className="form-page-title">🎉 Create New Event</h2>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Event Name *</label>
                      <input className="form-input"
                        value={evtForm.name}
                        onChange={e => setEvtForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Annual Sports Day" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Event Date *</label>
                      <input className="form-input" type="date"
                        value={evtForm.date}
                        onChange={e => setEvtForm(f => ({ ...f, date: e.target.value }))} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="evt-public-check">
                      <input type="checkbox"
                        checked={evtForm.isPublic}
                        onChange={e => setEvtForm(f => ({ ...f, isPublic: e.target.checked }))} />
                      <span>🌐 Make this event public (visible to parents on school page)</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Photos</label>
                    <div className="evt-upload-area" onClick={() => evtFileRef.current?.click()}>
                      <span style={{ fontSize: 28 }}>📷</span>
                      <p>Click to add photos</p>
                      <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>Max 5 MB per photo · 100 MB total per school</p>
                    </div>
                    <input ref={evtFileRef} type="file" multiple accept="image/*" style={{ display: 'none' }}
                      onChange={handleEvtImagePick} />
                    {evtImages.length > 0 && (
                      <div className="evt-preview-grid">
                        {evtImages.map((img, i) => (
                          <div key={i} className="evt-preview-thumb">
                            <img src={img.preview} alt={img.name} />
                            <button className="evt-preview-remove" onClick={() => setEvtImages(arr => arr.filter((_, j) => j !== i))}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {evtMsg && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, marginTop: 8,
                      background: evtMsg.startsWith('❌') ? '#fee2e2' : '#ede9fe',
                      color: evtMsg.startsWith('❌') ? '#991b1b' : '#5b21b6', fontSize: 14 }}>
                      {evtMsg}
                    </div>
                  )}

                  <div className="form-actions" style={{ marginTop: 20 }}>
                    <button className="btn btn-outline" onClick={() => setTab('events')} disabled={evtCreating}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleCreateEvent} disabled={evtCreating}>
                      {evtCreating ? 'Creating…' : '🎉 Create Event'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── EDIT EVENT ──────────────────────────────────── */}
              {tab === 'edit-event' && editEvt && (
                <div className="form-page">
                  <button className="btn btn-ghost back-btn" onClick={() => setTab('events')}>← Back to Events</button>
                  <h2 className="form-page-title">✏️ Edit Event</h2>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Event Name *</label>
                      <input className="form-input"
                        value={editForm.name}
                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Annual Sports Day" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Event Date *</label>
                      <input className="form-input" type="date"
                        value={editForm.date}
                        onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="evt-public-check">
                      <input type="checkbox"
                        checked={editForm.isPublic}
                        onChange={e => setEditForm(f => ({ ...f, isPublic: e.target.checked }))} />
                      <span>🌐 Make this event public (visible to parents on school page)</span>
                    </label>
                  </div>

                  {/* Existing photos */}
                  {editEvt.images && editEvt.images.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">
                        Existing Photos
                        {editDelPaths.length > 0 && (
                          <span style={{ color: 'var(--danger)', fontWeight: 600, marginLeft: 8, fontSize: 12 }}>
                            ({editDelPaths.length} marked for deletion)
                          </span>
                        )}
                      </label>
                      <div className="evt-preview-grid">
                        {editEvt.images.map((img, i) => {
                          const markedForDelete = editDelPaths.includes(img.path);
                          return (
                            <div key={i} className={`evt-preview-thumb${markedForDelete ? ' evt-preview-thumb--deleted' : ''}`}>
                              <img src={img.url} alt={`Photo ${i + 1}`} />
                              <button
                                className={`evt-preview-remove${markedForDelete ? ' evt-preview-remove--undo' : ''}`}
                                title={markedForDelete ? 'Undo delete' : 'Delete this photo'}
                                onClick={() => {
                                  if (markedForDelete) {
                                    setEditDelPaths(paths => paths.filter(p => p !== img.path));
                                  } else {
                                    setEditDelPaths(paths => [...paths, img.path]);
                                  }
                                }}
                              >
                                {markedForDelete ? '↩' : '✕'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Add new photos */}
                  <div className="form-group">
                    <label className="form-label">Add More Photos</label>
                    <div className="evt-upload-area" onClick={() => editFileRef.current?.click()}>
                      <span style={{ fontSize: 28 }}>📷</span>
                      <p>Click to add more photos</p>
                      <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>Max 5 MB per photo · 100 MB total per school</p>
                    </div>
                    <input ref={editFileRef} type="file" multiple accept="image/*" style={{ display: 'none' }}
                      onChange={handleEditImagePick} />
                    {editNewImages.length > 0 && (
                      <div className="evt-preview-grid" style={{ marginTop: 10 }}>
                        {editNewImages.map((img, i) => (
                          <div key={i} className="evt-preview-thumb">
                            <img src={img.preview} alt={img.name} />
                            <button className="evt-preview-remove" onClick={() => setEditNewImages(arr => arr.filter((_, j) => j !== i))}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {editMsg && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, marginTop: 8,
                      background: editMsg.startsWith('❌') ? '#fee2e2' : '#ede9fe',
                      color: editMsg.startsWith('❌') ? '#991b1b' : '#5b21b6', fontSize: 14 }}>
                      {editMsg}
                    </div>
                  )}

                  <div className="form-actions" style={{ marginTop: 20 }}>
                    <button className="btn btn-outline" onClick={() => setTab('events')} disabled={editSaving}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSaveEdit} disabled={editSaving}>
                      {editSaving ? 'Saving…' : '✅ Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── CANCEL BOOKING (inline page) ────────────────── */}
              {tab === 'cancel-booking' && cancelBooking && (
                <div className="form-page">
                  <button className="btn btn-ghost back-btn" onClick={() => { setCancelBooking(null); setTab(prevTab); }}>
                    ← Back
                  </button>
                  <h2 className="form-page-title">❌ Cancel Booking</h2>
                  <div className="confirm-danger-box">
                    <p>You are about to cancel the booking for <strong>{cancelBooking.childName}</strong> on <strong>{cancelBooking.date}</strong>.</p>
                    <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8 }}>The parent will be notified of the cancellation.</p>
                  </div>
                  <div className="form-group" style={{ marginTop: 20 }}>
                    <label className="form-label">Reason (optional)</label>
                    <textarea
                      className="form-textarea"
                      placeholder="e.g. School closed on this date, capacity full…"
                      value={cancelReason}
                      onChange={e => setCancelReason(e.target.value)}
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn btn-outline" onClick={() => { setCancelBooking(null); setTab(prevTab); }}>Back</button>
                    <button className="btn btn-danger" onClick={handleCancel} disabled={saving}>{saving ? 'Cancelling…' : 'Confirm Cancel'}</button>
                  </div>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
}
